import React, { Fragment, useState } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  NoDataFound,
  TableActionRow,
} from "./tableComponent";
import TableSkeleton from "../skeleton/tableSkeleton";
import { useSelector } from "react-redux";
import PaginationComponent from "./tablePagination";
import { IconActionButton } from "../action/iconActionButton";
import ConfirmModal from "../modals/ConfirmModel";
import DefaultBadge from "../badge/defaultBadge";
import { Icons } from "../../../utils/icons";
import {
  generateRowKey,
  generateHeaderKey,
  generateCellKey,
} from "../../../utils/tableKeyHelpers";
import {
  generateAvatarInitials,
  getAvatarBackgroundColor,
} from "../../../utils/helper";
import { Tooltip } from "flowbite-react";

const CommonTable = ({
  TableHeaderData = [],
  tabledata = [],
  loading = false,
  showActions = true,
  noDataText = "No Data Found!",
  currentPage,
  totalData,
  setCurrentPage,
  action,
  actionTooltips = {},
  actions = [],
  selectionKey,
  selectedRows = [],
  search,
  onSelectionChange = () => {},
}) => {
  const { limit } = useSelector((state) => state.global);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Helper function to get header text
  const getHeaderText = (header, index) => {
    if (typeof header === "string") return header;
    if (typeof header?.label === "string") return header.label;
    return `Column ${index + 1}`;
  };

  // Helper function to render checkbox
  const renderCheckbox = (checked, onChange, className = "cursor-pointer") => (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={className}
      style={{ cursor: "pointer" }}
    />
  );

  // Helper function to render profile data
  const renderProfileData = (data) => (
    <div className="flex items-center gap-2">
      {data.image && (
        <img
          src={data.image}
          alt={data.name}
          className="w-8 h-8 rounded-full object-cover"
        />
      )}
      <div className="flex flex-col">
        <span className="font-medium text-sm">{data.name}</span>
        {data.email && (
          <span className="text-xs text-gray-500">{data.email}</span>
        )}
      </div>
    </div>
  );

  // Helper function to render cell content
  const renderCellContent = (
    cellData,
    isFirstColWithCheckbox,
    tableRowData
  ) => {
    if (isFirstColWithCheckbox) {
      const isSelected = selectedRows.includes(tableRowData[selectionKey]);
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            disabled={tableRowData.disableCheckbox || false}
            onChange={() => {
              if (tableRowData.disableCheckbox) return;
              const newSelection = isSelected
                ? selectedRows.filter((id) => id !== tableRowData[selectionKey])
                : [...selectedRows, tableRowData[selectionKey]];
              onSelectionChange(newSelection);
            }}
            className={
              tableRowData.disableCheckbox
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer"
            }
            style={{
              cursor: tableRowData.disableCheckbox ? "not-allowed" : "pointer",
            }}
          />
          <span>{cellData || "-"}</span>
        </div>
      );
    }
    return cellData || "-";
  };

  // Helper function to render complex cell data
  const renderComplexCellData = (
    cellData,
    isFirstColWithCheckbox,
    tableRowData,
    header = null
  ) => {
    const mergedCellData =
      cellData &&
      typeof cellData === "object" &&
      header?.type &&
      cellData.variant === undefined
        ? { ...cellData, variant: header.type }
        : cellData;

    const { data, className, sticky, variant, customCases, ...otherProps } =
      mergedCellData;

    const adjustedVariant =
      (variant === "text" || variant === "checkbox-profile") &&
      isFirstColWithCheckbox
        ? "checkbox"
        : variant;
    // if(adjustedVariant==="profile"){
    // }

    const isSelected = selectedRows.includes(tableRowData[selectionKey]);

    if (variant === "group-avatar") {
      return (
        <TableCell
          className={className}
          sticky={sticky}
          variant={variant}
          {...otherProps}
        >
          <div className="flex -space-x-2">
            {Array.isArray(data) && data.length > 0 ? (
              data.map((user, idx) => {
                const hasImage =
                  user.profile_image &&
                  typeof user.profile_image === "string" &&
                  user.profile_image.trim() !== "";
                const initials = generateAvatarInitials(user.name);
                const bgColor = getAvatarBackgroundColor(user.name);

                return (
                  <div
                    key={idx}
                    className="w-8 h-8 rounded-full border-2 border-white overflow-hidden flex items-center justify-center"
                    title={user.name}
                  >
                    {hasImage ? (
                      <img
                        src={user.profile_image}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className={`w-full h-full ${bgColor} flex items-center justify-center`}
                      >
                        <span className="text-white text-xs font-semibold">
                          {initials}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <span className="text-gray-400 text-sm">-</span>
            )}
          </div>
        </TableCell>
      );
    }
    return (
      <TableCell
        className={className}
        sticky={sticky}
        variant={adjustedVariant}
        customCases={customCases}
        data={data}
        {...otherProps}
      >
        {isFirstColWithCheckbox && adjustedVariant === "checkbox" ? (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isSelected}
              disabled={tableRowData.disableCheckbox || false}
              onChange={() => {
                if (tableRowData.disableCheckbox) return;
                const newSelection = isSelected
                  ? selectedRows.filter(
                      (id) => id !== tableRowData[selectionKey]
                    )
                  : [...selectedRows, tableRowData[selectionKey]];
                onSelectionChange(newSelection);
              }}
              className={
                tableRowData.disableCheckbox
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              }
              style={{
                cursor: tableRowData.disableCheckbox
                  ? "not-allowed"
                  : "pointer",
              }}
            />
            {variant === "checkbox-profile" && typeof data === "object" ? (
              renderProfileData(data)
            ) : (
              <span>{typeof data === "string" ? data : "-"}</span>
            )}
          </div>
        ) : adjustedVariant === "input" ? (
          // For input variant, render the input directly
          data
        ) : (
          data || "-"
        )}
      </TableCell>
    );
  };

  // Generate action buttons with flexible visibility control
  const generateActionButtons = (item) => {
    const actionButtons = [];

    const actionConfigs = [
      { key: "view", tooltip: "View" },
      { key: "add", tooltip: "Add" },
      { key: "edit", tooltip: "Edit" },
      { key: "download", tooltip: "Download" },
      { key: "delete", tooltip: "Delete" },
      { key: "expire", tooltip: "Expire" },
      { key: "assign", tooltip: "Assign" },
      { key: "clear", tooltip: "Clear" },
      { key: "active", tooltip: "Inactive" },
      { key: "inactive", tooltip: "Active" },
      { key: "onhold", tooltip: "Onhold" },
      { key: "reopen", tooltip: "Reopen" },
      { key: "cancel", tooltip: "Cancel" },
      { key: "publish", tooltip: "Publish" },
    ];

    actionConfigs.forEach(({ key, tooltip }) => {
      // Check if action function exists
      if (!action?.[key]) return;

      // Check visibility using flexible show function pattern
      const showFunctionKey = `show${
        key.charAt(0).toUpperCase() + key.slice(1)
      }`;

      // If specific show function exists, use it
      if (action?.[showFunctionKey]) {
        if (!action[showFunctionKey](item)) {
          return; // Don't show this button
        }
      }
      // Fallback for delete button (backward compatibility)
      else if (key === "delete") {
        if (action?.showDelete && !action.showDelete(item)) {
          return;
        } else if (!action?.showDelete && item?.status?.data === 3) {
          return;
        }
      }

      // Add button to actions (allow page-level tooltip override via actionTooltips)
      const resolvedTooltip = actionTooltips[key] ?? tooltip;
      actionButtons.push({
        name: key,
        tooltip: resolvedTooltip,
        onClick:
          key === "delete"
            ? () => {
                setSelectedItem(item);
                setIsOpen(true);
              }
            : () => action[key](item),
      });
    });

    return actionButtons;
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    if (checked) {
      // Only select rows that are not disabled
      const selectableRows =
        tabledata
          ?.filter((row) => !row.disableCheckbox)
          .map((row) => row[selectionKey]) || [];
      onSelectionChange(selectableRows);
    } else {
      onSelectionChange([]);
    }
  };

  const hasSelection = selectionKey && selectedRows.length > 0;

  // Count only selectable (non-disabled) rows for "all selected" state
  const selectableRowsCount =
    tabledata?.filter((row) => !row.disableCheckbox).length || 0;
  const allSelected =
    selectableRowsCount > 0 && selectedRows.length === selectableRowsCount;

  return (
    <div className="relative pb-14">
      <Table>
        <TableHeader>
          <TableRow>
            {TableHeaderData.map((header, index) => {
              const isFirstColWithCheckbox = selectionKey && index === 0;
              return (
                <TableHead
                  key={generateHeaderKey(header, index, "common-table-header")}
                >
                  {isFirstColWithCheckbox ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="cursor-pointer"
                        style={{ cursor: "pointer" }}
                      />
                      <span>{getHeaderText(header, index)}</span>
                    </div>
                  ) : (
                    getHeaderText(header, index)
                  )}
                </TableHead>
              );
            })}
            {/* {showActions && (
              <TableHead className="sticky text-center right-0 bg-light z-20 min-w-[120px]">
                Actions
              </TableHead>
            )} */}
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableSkeleton
              // colSpan={TableHeaderData.length + (showActions ? 1 : 0)}
              colSpan={TableHeaderData?.length}
            />
          ) : tabledata?.length === 0 ? (
            <TableRow>
              <NoDataFound
                search={search}
                colSpan={TableHeaderData?.length}
                text={noDataText}
              />
            </TableRow>
          ) : (
            tabledata.map((tableRowData, rowIndex) => (
              <TableRow
                key={generateRowKey(tableRowData, rowIndex, "common-table-row")}
              >
                {TableHeaderData.map((header, cellIndex) => {
                  const isFirstColWithCheckbox =
                    selectionKey && cellIndex === 0;
                  const { key } = header;
                  const cellData = tableRowData[key];

                  if (
                    cellData &&
                    typeof cellData === "object" &&
                    !React.isValidElement(cellData)
                  ) {
                    return (
                      <React.Fragment
                        key={generateCellKey(
                          tableRowData,
                          rowIndex,
                          key,
                          cellIndex,
                          "complex-cell"
                        )}
                      >
                        {renderComplexCellData(
                          cellData,
                          isFirstColWithCheckbox,
                          tableRowData,
                          header
                        )}
                      </React.Fragment>
                    );
                  }

                  return (
                    <TableCell
                      key={generateCellKey(
                        tableRowData,
                        rowIndex,
                        key,
                        cellIndex,
                        "simple-cell"
                      )}
                    >
                      {renderCellContent(
                        cellData,
                        isFirstColWithCheckbox,
                        tableRowData
                      )}
                    </TableCell>
                  );
                })}

                {showActions && (
                  <TableActionRow className="sticky right-0 bg-white z-20">
                    {action?.isSystemGenerated &&
                    action.isSystemGenerated(tableRowData) ? (
                      // Show system generated badge instead of action buttons
                      <div className="flex items-center">
                        <DefaultBadge
                          status={1}
                          custom={
                            action.systemGeneratedBadge?.text ||
                            "System Generated"
                          }
                          background="bg-blue-50"
                          customCases={{
                            1:
                              action.systemGeneratedBadge?.text ||
                              "System Generated",
                          }}
                        />
                      </div>
                    ) : (
                      <IconActionButton
                        actions={generateActionButtons(tableRowData)}
                      />
                    )}
                  </TableActionRow>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Sticky pagination — stays visible at the bottom while scrolling */}
      <div className="sticky bottom-0 z-20 bg-white border-t border-gray-100 shadow-[0_-4px_16px_rgba(0,0,0,0.05)]">
        <PaginationComponent
          itemPerPage={limit}
          currentPage={currentPage}
          totalItems={totalData}
          paginate={setCurrentPage}
          loading={loading}
        />
      </div>

      <ConfirmModal
        isOpen={isOpen}
        title="Attention!"
        message="Are you sure you want to delete this item?"
        loading={deleteLoading}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            if (action?.delete) {
              await action.delete(selectedItem); // wait for API or async work
            }
            setIsOpen(false);
          } finally {
            setDeleteLoading(false);
          }
        }}
        onCancel={() => setIsOpen(false)}
      />

      {hasSelection && (
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-xl z-30 flex items-center px-6 py-3 border border-gray-200 gap-6">
          <span className="text-sm font-medium text-primary-700">
            {selectedRows.length} Items Selected
          </span>

          <div className="w-px h-5 bg-gray-200" />

          <button
            onClick={() => onSelectionChange([])}
            className="text-sm font-semibold text-orange-500 hover:underline flex items-center gap-1"
          >
            Unselect All
          </button>

          {/* {actions.length > 0 && (
            <>
              <div className="w-px h-5 bg-gray-300" />
              {actions.map(
                ({ label, icon, onClick, color = "text-primary-800" }, idx) => (
                  <React.Fragment key={idx}>
                    <button
                      onClick={() => onClick(selectedRows)}
                      className={`text-sm font-semibold ${color} flex items-center gap-1`}
                    >
                      {icon}
                      {label}
                    </button>
                    {idx < actions.length - 1 && (
                      <div className="w-px h-5 bg-gray-300" />
                    )}
                  </React.Fragment>
                )
              )}
            </>
          )} */}

          {actions.length > 0 && (
            <>
              <div className="w-px h-5 bg-gray-300" />

              {actions.map(
                ({ label, icon, onClick,content, color = "text-primary-800" }, idx) => (
                  <React.Fragment key={idx}>
                    <button
                      onClick={() => onClick(selectedRows)}
                      className={`text-sm font-semibold ${color} flex items-center gap-2 relative`}
                    >
                      {/* Icon with inline tooltip */}
                      <span className="relative group inline-flex items-center justify-center p-1">
                        {icon}
                        <span
                          className="
          absolute bottom-full left-1/2 -translate-x-1/2 mb-2
          whitespace-nowrap px-3 py-1
          text-xs text-white bg-gray-900 rounded shadow-lg
          opacity-0 group-hover:opacity-100
          transition-all duration-200
          pointer-events-none
          z-50
        "
                        >
                          {content}
                        </span>
                      </span>

                      {/* Optional label */}
                      {label && <span>{label}</span>}
                    </button>

                    {idx < actions.length - 1 && (
                      <div className="w-px h-5 bg-gray-300" />
                    )}
                  </React.Fragment>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CommonTable;
